import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';

import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import messages from './messages';
import styles from './style.module.less';
import { getUpsellModalContent } from '../../../../../components/upsell-modal/helper';
import { ReturnRabbit } from '../../../../../constants/upsell-modal-content-codes';
import { useGetLink } from '../../../../../hooks/use-get-link';
import { Segment } from '../../../../../services/analytics-services/segment';
import {
  ADD_ONS_WITH_SETTINGS,
  ADD_ON_KNOWLEDGE_BASE_CLICKED,
  ADD_ON_CONFIGURE_SETTINGS_CLICKED,
  ACTION_MENU,
} from '../../../add-on-marketplace/constants';

// TODO: terrible pattern for time sake
const getManageSettingsText = (addOnCode) => {
  switch (addOnCode) {
    case ReturnRabbit:
      return messages.openReturnRabbit;
    default:
      return messages.manageSettings;
  }
};

/**
 * @typedef {import('../../../../../prop-types/add-on.types').AddOnPropType} AddOn
 */

/**
 * @param {object} props
 * @param {AddOn} props.addOn
 * @param {() => void} props.onClickDeactivate
 * @param {(code: string) => void} props.onClickManageSettings
 * @param {() => void} props.onClickReactivate
 * @param {boolean} props.shouldShowManageSettingsOption
 * @returns {JSX.Element}
 */
export const AddOnActionMenu = ({
  onClickDeactivate,
  addOn,
  onClickManageSettings = noop,
  onClickReactivate,
  shouldShowManageSettingsOption = false,
}) => {
  const intl = useIntl();
  const content = getUpsellModalContent(addOn.code);
  const knowledgeBaseLink = useGetLink(content.kbArticle);

  const isAddOnIncludedWithPlan = addOn?.isIncludedInPlan;
  const isAlphaUser = addOn?.isAlphaUser;
  const isIncluded = isAddOnIncludedWithPlan || isAlphaUser;
  const shouldShowReactivateAction = !!addOn?.deactivationDateTimeUTC && !isIncluded;
  const shouldShowDeactivateButton = !shouldShowReactivateAction && !isIncluded;
  const isReturnRabbit = addOn.code === ReturnRabbit;

  const onClickKBLink = useCallback(() => {
    Segment.trackEvent(ADD_ON_KNOWLEDGE_BASE_CLICKED, {
      addOnCode: addOn.code,
    });
  }, [addOn.code]);

  const onClickManageSettingsForAddOn = useCallback(() => {
    Segment.trackEvent(ADD_ON_CONFIGURE_SETTINGS_CLICKED, {
      addOnCode: addOn.code,
      source: ACTION_MENU,
    });
    onClickManageSettings(addOn.code);
  }, [onClickManageSettings, addOn.code]);

  return (
    <DropdownMenu dropdownMenuClassName={styles['action-menu-wrapper']}>
      <DropdownMenuToggler
        ariaLabel={intl.formatMessage(messages.addOnMenuAriaLabel)}
        className={styles['actions-dropdown']}
        hideChevron
        openClassName={styles['icon-open']}
        theme='unstyled'
      >
        <FontAwesomeIcon className={styles.ellipsis} icon={faEllipsisH} />
      </DropdownMenuToggler>
      {shouldShowManageSettingsOption && ADD_ONS_WITH_SETTINGS.includes(addOn.code) ? (
        <DropdownMenuItem onClick={onClickManageSettingsForAddOn}>
          <Text textType={TextType.Caption} value={getManageSettingsText(addOn.code)} />
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem className={styles['learn-more-link']} href={knowledgeBaseLink} onClick={onClickKBLink}>
        <Text textType={TextType.Caption} value={messages.learnMore} />
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </DropdownMenuItem>
      {shouldShowReactivateAction ? (
        <DropdownMenuItem className={styles.reactivate} onClick={onClickReactivate}>
          <Text textType={TextType.Caption} value={messages.reactivate} />
        </DropdownMenuItem>
      ) : null}
      {shouldShowDeactivateButton && !isReturnRabbit ? (
        <DropdownMenuItem className={styles.deactivate} onClick={onClickDeactivate}>
          <Text textType={TextType.Caption} value={messages.deactivate} />
        </DropdownMenuItem>
      ) : null}
    </DropdownMenu>
  );
};
